import { ArticleHeading2, Label, Paragraph2, UIHeading2 } from '@cian/ui-kit';
import classNames from 'classnames';
import React, { memo, useMemo } from 'react';

import styles from './Price.css';
import { IDiscount } from '../../types/offer';
import { ContentRow } from '../ContentRow';
import { GeneralInfoSectionRow } from '../GeneralInfoSectionRow';
import { getEtazhiBlackFridayPriceDiscountColors } from 'shared/utils/etazhiBlackFriday';

import type { IEtazhiBlackFridayLabel } from 'shared/types/etazhiBlackFriday';

interface IPriceProps {
  priceInfo?: string;
  price: string;
  discount?: IDiscount;
  isHighlighted?: boolean;
  isMuted?: boolean;
  MortgageAdFoxBanner?: React.ReactNode;
  mortgagePayment?: React.ReactNode;

  /**
   * Код появился к проекту "Чёрная пятница" для компании "Этажи".
   * @todo: удалить в задаче CD-250843
   */
  etazhiBlackFridayDiscount?: IDiscount | null;
  etazhiBlackFridayLabelData?: IEtazhiBlackFridayLabel | null;
}

const defaultColors = {
  bgColor: 'accent-positive-primary',
  color: 'text-inverted-default',
  strikethroughColor: 'accent-positive-primary',
};

/** Компонент цены */
export const PriceComponent: React.FC<IPriceProps> = ({
  priceInfo,
  price,
  isHighlighted,
  isMuted,
  MortgageAdFoxBanner,
  discount,
  mortgagePayment,

  /**
   * Код появился к проекту "Чёрная пятница" для компании "Этажи".
   * @todo: удалить в задаче CD-250843
   */
  etazhiBlackFridayDiscount,
  etazhiBlackFridayLabelData,
}) => {
  const discountData = discount || etazhiBlackFridayDiscount;

  /**
   * Код появился к проекту "Чёрная пятница" для компании "Этажи".
   * @todo: удалить в задаче CD-250843
   */
  const discountColors = useMemo(() => {
    if (!etazhiBlackFridayDiscount || !etazhiBlackFridayLabelData?.color) {
      return defaultColors;
    }

    return getEtazhiBlackFridayPriceDiscountColors(etazhiBlackFridayLabelData.color);
  }, [etazhiBlackFridayDiscount, etazhiBlackFridayLabelData?.color]);

  return (
    <GeneralInfoSectionRow>
      <div className={styles['price-wrapper']}>
        <div>
          <ContentRow>
            {discountData ? (
              <div className={styles['price-container']} data-testid="offer-discount-block">
                <ArticleHeading2
                  color={isMuted ? 'text-secondary-default' : 'text-primary-default'}
                  data-mark="DiscountPrice"
                >
                  <span
                    className={classNames(isHighlighted && /* istanbul ignore next*/ styles['highlighted'])}
                    data-testid="offer-discount-new-price"
                  >
                    {discountData.newPrice}
                  </span>
                </ArticleHeading2>
                <Label
                  background={discountColors.bgColor}
                  color={discountColors.color}
                  data-testid="offer-discount-label"
                >
                  {discountData.percent}
                </Label>
                <span
                  className={styles['strikethrough']}
                  data-testid="offer-discount-old-price"
                  style={{ textDecorationColor: `var(--${discountColors.strikethroughColor})` }}
                >
                  {discountData.oldPrice}
                </span>
              </div>
            ) : (
              <UIHeading2 data-mark="MainPrice" color={isMuted ? 'text-secondary-default' : 'text-primary-default'}>
                <span className={classNames(isHighlighted && /* istanbul ignore next*/ styles['highlighted'])}>
                  {price}
                </span>
              </UIHeading2>
            )}
          </ContentRow>
          {priceInfo && (
            <ContentRow>
              <Paragraph2 data-mark="PriceInfo" color="gray60_100">
                {priceInfo}
              </Paragraph2>
            </ContentRow>
          )}
        </div>

        {mortgagePayment}
      </div>
      {MortgageAdFoxBanner}
    </GeneralInfoSectionRow>
  );
};

PriceComponent.displayName = 'Price';

/** @see {@link PriceComponent} */
export const Price = memo(PriceComponent);
